import type { LineSelectionInput, RfqDetail } from './types.js';

/**
 * Satır satır seçimin arayüz kuralları (04.11 notları D4; ADR 0166 §2). Seçenekler satır
 * karşılaştırmasından gelir ve sunucunun `resolveLineSelection` kuralıyla aynıdır: fiyatlı ya da
 * ikame yanıt (fiyatı olan), karşılaştırmaya giren hücre. Satırın fiyatlı en iyisi varken başka
 * seçim gerekçe ister. Reddeden ya da fiyatsız bırakan teklif o satırda seçenek değildir; teklifin
 * öteki satırları seçilebilir.
 */

export interface LineChoice {
  /** `<quotationId>:<kind>`; seçim kutusunun değeri. */
  value: string;
  quotationId: string;
  kind: 'priced' | 'substitute';
  supplierName: string;
  unitPrice: number;
  total: number | null;
  /** Satırın en iyi fiyatı (fiyatlı yanıt). */
  best: boolean;
  /** Seçilirse gerekçe gerekmez: en iyi ya da satırın fiyatlı en iyisi yok. */
  reasonFree: boolean;
  free: boolean;
}

/** RFQ kalemi → seçenekler (karşılaştırmadaki tedarikçi sırasıyla). */
export function lineChoices(detail: RfqDetail): Map<string, LineChoice[]> {
  const names = new Map(detail.quotations.map((q) => [q.id, q.supplierName]));
  const result = new Map<string, LineChoice[]>();
  for (const row of detail.lineComparison?.lines ?? []) {
    const choices: LineChoice[] = [];
    for (const cell of row.cells) {
      if (!cell.eligible || cell.unitPrice === null) continue;
      const kind =
        cell.response === 'priced' && !cell.substitute
          ? 'priced'
          : cell.response === 'substitute' && cell.substitute && cell.total !== null
            ? 'substitute'
            : null;
      if (kind === null) continue;
      choices.push({
        value: `${cell.quotationId}:${kind}`,
        quotationId: cell.quotationId,
        kind,
        supplierName: names.get(cell.quotationId) ?? '',
        unitPrice: cell.unitPrice,
        total: cell.total,
        best: kind === 'priced' && cell.best,
        reasonFree: row.bestUnitPrice === null || (kind === 'priced' && cell.best),
        free: cell.freeOfCharge === true,
      });
    }
    result.set(row.rfqLineId, choices);
  }
  return result;
}

/**
 * Varsayılan seçim: satırın en iyi fiyatı; bir teklif verildiyse yalnız onun fiyatlı satırları
 * (eksik teklifin "Select lines" kısayolu). Seçilemeyen ya da başka ödüldeki satır açık kalır.
 */
export function defaultLineValues(
  detail: RfqDetail,
  options: { quotationId?: string | null; awarded?: ReadonlySet<string> } = {},
): Record<string, string> {
  const choices = lineChoices(detail);
  const values: Record<string, string> = {};
  for (const line of detail.lines) {
    const own = options.awarded?.has(line.id) ? [] : (choices.get(line.id) ?? []);
    const pick = options.quotationId
      ? own.find((c) => c.quotationId === options.quotationId && c.kind === 'priced')
      : own.find((c) => c.best);
    values[line.id] = pick?.value ?? '';
  }
  return values;
}

/** Seçilen seçenekler RFQ kalem sırasıyla. */
function chosen(detail: RfqDetail, values: Record<string, string>) {
  const choices = lineChoices(detail);
  return detail.lines.flatMap((line) => {
    const choice = (choices.get(line.id) ?? []).find((c) => c.value === values[line.id]);
    return choice ? [{ line, choice }] : [];
  });
}

/** En ucuz olmayan bir seçim var mı (gerekçe zorunlu). */
export function lineSelectionNeedsReason(
  detail: RfqDetail,
  values: Record<string, string>,
): boolean {
  return chosen(detail, values).some(({ choice }) => !choice.reasonFree);
}

/** Tedarikçi başına özet: kalem sayısı ve kalem toplamı (her tedarikçiye bir taslak PO). */
export function lineSelectionSummary(detail: RfqDetail, values: Record<string, string>) {
  const groups = new Map<string, { supplierName: string; count: number; total: number }>();
  for (const { choice } of chosen(detail, values)) {
    const group = groups.get(choice.quotationId) ?? {
      supplierName: choice.supplierName,
      count: 0,
      total: 0,
    };
    group.count += 1;
    group.total = Math.round((group.total + (choice.total ?? 0)) * 100) / 100;
    groups.set(choice.quotationId, group);
  }
  return [...groups.values()];
}

/** Sunucuya giden seçim (`POST /api/office/rfqs/:id/line-selection`). */
export function toLineSelection(
  detail: RfqDetail,
  values: Record<string, string>,
  reason: string,
): LineSelectionInput {
  return {
    lines: chosen(detail, values).map(({ line, choice }) => ({
      rfqLineId: line.id,
      quotationId: choice.quotationId,
      kind: choice.kind,
    })),
    reason: reason.trim() || null,
  };
}
